import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useInternals } from '../../context/internalContext';
import { toast } from 'react-toastify';
import Modal from '../../components/modal';
import TooltipInfo from '../../components/TooltipInfo';
import useSubmitLock from '../../utils/useSubmitLock';
import BinaryFileLink from '../../components/BinaryFileLink';
import { BINARY_RESOURCES, hasBinaryField } from '../../utils/binaryFile';
import './factionRelationship.css';

const INITIAL_FACTION_FORM_DATA = {
  relationship: '',
  faction: '',
  funcao: '',
  registration: '',
  file: null,
};

const INITIAL_GODPARENT_DATA = {
  name: '',
  cpf: '',
  rg: '',
  observation: '',
  situation_with_justice: '',
};

const DEFAULT_RELATIONSHIP_OPTIONS = ['Integrante', 'Simpatizante', 'Companheiro'];

const FactionRelationship = ({ internalId, relationshipId }) => {
  const {
    listFactionrelationship,
    createFactionrelationship,
    updateFactionrelationship,
    listFactions,
    listFunctionFaction,
    listOtherPeople,
    createOtherPeople,
  } = useInternals();

  const [formData, setFormData] = useState({
    ...INITIAL_FACTION_FORM_DATA,
  });

  const [existingId, setExistingId] = useState(null);
  const [factions, setFactions] = useState([]);
  const [functions, setFunctions] = useState([]);
  // id do vínculo que já tem arquivo (o arquivo sai pela rota binary)
  const [currentFileId, setCurrentFileId] = useState(null);
  const [selectedGodparents, setSelectedGodparents] = useState([]);
  const [godparentQuery, setGodparentQuery] = useState('');
  const [godparentOptions, setGodparentOptions] = useState([]);
  const [isSearchingGodparents, setIsSearchingGodparents] = useState(false);
  const [isGodparentModalOpen, setIsGodparentModalOpen] = useState(false);
  const [newGodparentData, setNewGodparentData] = useState({ ...INITIAL_GODPARENT_DATA });
  const [isCreatingGodparent, setIsCreatingGodparent] = useState(false);
  const { isSubmitting, runWithSubmitLock } = useSubmitLock();

  const resetFactionRelationship = useCallback(() => {
    setExistingId(null);
    setFormData({ ...INITIAL_FACTION_FORM_DATA });
    setCurrentFileId(null);
    setSelectedGodparents([]);
  }, []);

  const normalizeOtherPerson = useCallback((person) => {
    if (!person) return null;

    if (typeof person === 'number' || typeof person === 'string') {
      return {
        id: String(person),
        name: `ID ${person}`,
        cpf: '',
      };
    }

    const idValue = person.id ?? person.pk;
    if (idValue === undefined || idValue === null || idValue === '') {
      return null;
    }

    return {
      id: String(idValue),
      name: person.name || person.social_name || `ID ${idValue}`,
      cpf: person.cpf || '',
    };
  }, []);

  const getOtherPeopleList = (response) => {
    if (Array.isArray(response?.results)) return response.results;
    if (Array.isArray(response)) return response;
    return [];
  };

  const selectedGodparentIds = useMemo(
    () => new Set(selectedGodparents.map((person) => String(person.id))),
    [selectedGodparents]
  );

  const relationshipOptions = useMemo(() => {
    const fromBackend = String(formData.relationship || '').trim();
    if (!fromBackend) return DEFAULT_RELATIONSHIP_OPTIONS;

    const hasOption = DEFAULT_RELATIONSHIP_OPTIONS.some(
      (option) => option.toLowerCase() === fromBackend.toLowerCase()
    );

    return hasOption
      ? DEFAULT_RELATIONSHIP_OPTIONS
      : [...DEFAULT_RELATIONSHIP_OPTIONS, fromBackend];
  }, [formData.relationship]);

  const addGodparentSelection = (person) => {
    const normalized = normalizeOtherPerson(person);
    if (!normalized) return;

    setSelectedGodparents((prev) => {
      if (prev.some((item) => String(item.id) === String(normalized.id))) {
        return prev;
      }
      return [...prev, normalized];
    });
  };

  const removeGodparentSelection = (personId) => {
    setSelectedGodparents((prev) => prev.filter((item) => String(item.id) !== String(personId)));
  };

  const applyFactionRelationship = useCallback((response) => {
    if (!response) {
      resetFactionRelationship();
      return;
    }

    const godparentsRaw = Array.isArray(response.godparents) ? response.godparents : [];
    const normalizedGodparents = godparentsRaw
      .map(normalizeOtherPerson)
      .filter(Boolean);

    setFormData({
      relationship: response.relationship || '',
      faction: response.faction?.id || response.faction || '',
      funcao: response.function?.id || response.function || '',
      registration: response.registration || '',
      file: null,
    });
    setCurrentFileId(hasBinaryField(response, 'file') ? response.id : null);
    setExistingId(response.id || null);
    setSelectedGodparents(normalizedGodparents);
  }, [normalizeOtherPerson, resetFactionRelationship]);

  useEffect(() => {
    const loadLists = async () => {
      try {
        const [factionsRes, functionsRes] = await Promise.all([
          listFactions(),
          listFunctionFaction(),
        ]);
        setFactions(factionsRes?.results || factionsRes || []);
        setFunctions(functionsRes?.results || functionsRes || []);
      } catch (err) {
        console.error('Erro ao carregar listas auxiliares', err);
      }
    };
    loadLists();
  }, [listFactions, listFunctionFaction]);

  useEffect(() => {
    const fetchData = async () => {
      if (!relationshipId) {
        resetFactionRelationship();
        return;
      }

      try {
        const response = await listFactionrelationship(relationshipId);
        applyFactionRelationship(response);
      } catch (err) {
        console.warn('Sem relação existente, modo criação.');
        resetFactionRelationship();
      }
    };
    fetchData();
  }, [relationshipId, listFactionrelationship, applyFactionRelationship, resetFactionRelationship]);

  useEffect(() => {
    const trimmedQuery = godparentQuery.trim();
    if (trimmedQuery.length < 2) {
      setGodparentOptions([]);
      setIsSearchingGodparents(false);
      return undefined;
    }

    let isMounted = true;
    setIsSearchingGodparents(true);

    const timeoutId = setTimeout(async () => {
      try {
        const responseWithName = await listOtherPeople({ name: trimmedQuery });
        const list = getOtherPeopleList(responseWithName);

        if (!isMounted) return;

        const normalized = list
          .map(normalizeOtherPerson)
          .filter(Boolean)
          .filter((item) => !selectedGodparentIds.has(String(item.id)));

        setGodparentOptions(normalized);
      } catch (error) {
        if (!isMounted) return;
        setGodparentOptions([]);
      } finally {
        if (isMounted) {
          setIsSearchingGodparents(false);
        }
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [godparentQuery, listOtherPeople, selectedGodparentIds, normalizeOtherPerson]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, file: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await runWithSubmitLock(async () => {
      const payloadObject = {};
      const godparentsIds = selectedGodparents
        .map((person) => String(person.id || '').trim())
        .filter(Boolean);

      if (formData.relationship) payloadObject.relationship = formData.relationship;
      if (formData.faction) payloadObject.faction = Number(formData.faction);
      if (formData.funcao) payloadObject.function = Number(formData.funcao);
      if (formData.registration) payloadObject.registration = formData.registration;
      if (internalId) payloadObject.internal = internalId;
      if (existingId || godparentsIds.length > 0) payloadObject.godparents = godparentsIds;

      const payload = formData.file ? new FormData() : payloadObject;
      if (formData.file) {
        Object.entries(payloadObject).forEach(([key, value]) => {
          if (key === 'godparents') return;
          payload.append(key, value);
        });
        godparentsIds.forEach((id) => {
          payload.append('godparents', id);
        });
        payload.append('file', formData.file);
      }

      try {
        let savedRelationship;
        if (existingId) {
          savedRelationship = await updateFactionrelationship(existingId, payload);
        } else {
          savedRelationship = await createFactionrelationship(payload);
        }

        const relationshipToRefresh = savedRelationship?.id || existingId;
        if (relationshipToRefresh) {
          const updatedRelationship = await listFactionrelationship(relationshipToRefresh);
          applyFactionRelationship(updatedRelationship);
        } else {
          applyFactionRelationship(savedRelationship);
        }
        setGodparentQuery('');
        setGodparentOptions([]);
      } catch (err) {
        toast.error('Erro ao salvar relação com facção');
        console.error(err);
      }
    });
  };

  const handleCreateGodparent = async (e) => {
    e.preventDefault();
    const name = newGodparentData.name.trim();
    const cpf = newGodparentData.cpf.replace(/\D/g, '');

    if (!name) {
      toast.warning('Informe o nome do padrinho.');
      return;
    }

    if (cpf.length !== 11) {
      toast.warning('Informe um CPF válido com 11 dígitos.');
      return;
    }

    const payload = {
      name,
      cpf,
      rg: newGodparentData.rg.trim(),
      observation: newGodparentData.observation.trim(),
      situation_with_justice: newGodparentData.situation_with_justice.trim(),
      is_active: true,
      already_arrested: false,
    };

    try {
      setIsCreatingGodparent(true);
      const created = await createOtherPeople(payload);
      if (created?.id) {
        addGodparentSelection(created);
        setIsGodparentModalOpen(false);
        setNewGodparentData({ ...INITIAL_GODPARENT_DATA });
        setGodparentQuery('');
        setGodparentOptions([]);
      }
    } finally {
      setIsCreatingGodparent(false);
    }
  };

  return (
    <div className="form-section">
      <form onSubmit={handleSubmit}>
        <div className="div-form-container">
          <div className="form-group">
            <label className="form-label">Facção:</label>
            <select
              name="faction"
              value={formData.faction}
              onChange={handleChange}
              className="form-input"
            >
              <option value="">Selecione</option>
              {factions.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Relação com a facção:</label>
            <select
              name="relationship"
              value={formData.relationship}
              onChange={handleChange}
              className="form-input"
            >
              <option value="">Selecione</option>
              {relationshipOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Função:</label>
            <select
              name="funcao"
              value={formData.funcao}
              onChange={handleChange}
              className="form-input"
            >
              <option value="">Selecione</option>
              {functions.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Matrícula:</label>
            <input
              type="text"
              name="registration"
              value={formData.registration}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <div className="faction-godparent-header">
              <label className="form-label faction-godparent-label">
                Padrinhos
                <TooltipInfo text="Digite parte do nome e clique na tag azul para adicionar. Clique no padrinho selecionado para remover." />
              </label>
              <button
                type="button"
                className="faction-godparent-create-btn"
                onClick={() => setIsGodparentModalOpen(true)}
              >
                + Novo padrinho
              </button>
            </div>

            <div className="faction-godparent-search">
              <input
                type="text"
                value={godparentQuery}
                onChange={(e) => setGodparentQuery(e.target.value)}
                className="form-input"
                placeholder="Buscar padrinho por nome"
              />
            </div>

            {isSearchingGodparents && (
              <small className="faction-godparent-helper">Buscando padrinhos...</small>
            )}

            {!isSearchingGodparents && godparentOptions.length > 0 && (
              <div className="faction-godparent-suggestions" role="listbox">
                {godparentOptions.map((person) => (
                  <button
                    type="button"
                    key={person.id}
                    className="faction-godparent-suggestion-item"
                    onClick={() => {
                      addGodparentSelection(person);
                      setGodparentOptions((prev) => prev.filter((item) => String(item.id) !== String(person.id)));
                    }}
                  >
                    {person.name} {person.cpf ? `- ${person.cpf}` : ''}
                  </button>
                ))}
              </div>
            )}

            {selectedGodparents.length > 0 ? (
              <div className="faction-godparent-selected">
                {selectedGodparents.map((person) => (
                  <button
                    type="button"
                    key={person.id}
                    className="faction-godparent-selected-item"
                    onClick={() => removeGodparentSelection(person.id)}
                    title="Clique para remover"
                  >
                    <span>{person.name}</span>
                    <span className="faction-godparent-remove">x</span>
                  </button>
                ))}
              </div>
            ) : (
              <small className="faction-godparent-helper">Nenhum padrinho selecionado.</small>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Arquivo:</label>
            {currentFileId && (
              <div className="file-preview">
                <BinaryFileLink
                  resource={BINARY_RESOURCES.factionRelationship}
                  id={currentFileId}
                  field="file"
                  className="file-link"
                >
                  📎 Ver arquivo atual
                </BinaryFileLink>
              </div>
            )}
            <input
              type="file"
              onChange={handleFileChange}
              className="form-input"
            />
          </div>
        </div>

        <button type="submit" className="btn btn-primary faction-save-btn" disabled={isSubmitting}>
          {isSubmitting ? 'Salvando...' : existingId ? 'Atualizar' : 'Cadastrar'}
        </button>
      </form>

      <Modal
        isOpen={isGodparentModalOpen}
        closeModal={() => {
          setIsGodparentModalOpen(false);
          setNewGodparentData({ ...INITIAL_GODPARENT_DATA });
        }}
        title="Cadastrar padrinho"
      >
        <form onSubmit={handleCreateGodparent} className="div-form-container">
          <div className="form-group">
            <label className="form-label">Nome:</label>
            <input
              type="text"
              value={newGodparentData.name}
              onChange={(e) => setNewGodparentData((prev) => ({ ...prev, name: e.target.value }))}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">CPF:</label>
            <input
              type="text"
              value={newGodparentData.cpf}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                setNewGodparentData((prev) => ({ ...prev, cpf: value }));
              }}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">RG:</label>
            <input
              type="text"
              value={newGodparentData.rg}
              onChange={(e) => setNewGodparentData((prev) => ({ ...prev, rg: e.target.value }))}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Situação com a justiça:</label>
            <input
              type="text"
              value={newGodparentData.situation_with_justice}
              onChange={(e) => setNewGodparentData((prev) => ({ ...prev, situation_with_justice: e.target.value }))}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Observação:</label>
            <textarea
              value={newGodparentData.observation}
              onChange={(e) => setNewGodparentData((prev) => ({ ...prev, observation: e.target.value }))}
              className="form-input"
              rows={3}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={isCreatingGodparent}>
            {isCreatingGodparent ? 'Salvando...' : 'Salvar padrinho'}
          </button>
        </form>
      </Modal>
    </div>
  );
};

export default FactionRelationship;
